/**
 * Waiting room (UX-SPEC §4): the big tile-lettered room code, Copy link and
 * Share, a gentle loom animation while we wait, the settings summary and
 * Leave. The game screen takes over the moment the opponent joins.
 */
import type { RoomView } from '@wordloom/engine';
import { canShare, shareRoom } from '../lib/share';
import { turnTimeLabel } from '../lib/time';
import { useCopyRoomLink } from '../lib/useCopyRoomLink';
import { leave } from '../net/session';
import { roomUrl } from '../router';
import { IconCheck, IconCopy, IconDoor, IconShare } from './Icons';
import { MiniTile } from './Tile';
import { Tooltip } from './Tooltip';
import { VersionPill } from './VersionPill';
import './WaitingRoom.css';

/** Warp threads with a shuttle weaving rows of weft — decorative only. */
function Loom() {
  const warps = Array.from({ length: 9 }, (_, i) => 20 + i * 20);
  const rows = [34, 50, 66, 82];
  return (
    <svg className="loom" viewBox="0 0 200 110" aria-hidden="true" focusable="false">
      <rect x="8" y="14" width="184" height="6" rx="3" className="loom__beam" />
      <rect x="8" y="94" width="184" height="6" rx="3" className="loom__beam" />
      {warps.map((x) => (
        <line key={x} x1={x} y1="20" x2={x} y2="94" className="loom__warp" />
      ))}
      {rows.map((y, i) => (
        <path
          key={y}
          className="loom__weft"
          style={{ animationDelay: `${i * 1.2}s` }}
          d={`M16 ${y} ${warps.map((x, j) => `Q ${x - 10} ${y + (j % 2 ? 3 : -3)} ${x} ${y}`).join(' ')} L 184 ${y}`}
          pathLength={1}
        />
      ))}
      <g className="loom__shuttle">
        <rect x="-11" y="-4" width="22" height="8" rx="4" />
      </g>
    </svg>
  );
}

export function WaitingRoom({ view }: { view: RoomView }) {
  const me = view.seats[view.you];
  const { turnTimeLimitSec, passRoundsToEnd } = view.settings;
  const url = roomUrl(view.code);
  const { copied, copy } = useCopyRoomLink(view.code);
  return (
    <div className="waiting-screen">
      <main className="screen-center" id="main">
        <section className="card waiting" aria-labelledby="waiting-title">
          <p className="eyebrow">Room code</p>
          <h1 id="waiting-title" className="sr-only">
            Room {view.code} — waiting for your opponent
          </h1>
          {/* The tiles are a picture of the code: role="img" makes the spelled-out
              label valid (aria-label on a plain <p> is prohibited and ignored). */}
          <p className="waiting__code" role="img" aria-label={`Room code ${[...view.code].join(' ')}`}>
            {[...view.code].map((ch, i) => (
              <MiniTile key={i} letter={ch} className="waiting__tile" />
            ))}
          </p>

          <div className="waiting__share">
            <input
              className="input waiting__url"
              value={url}
              readOnly
              aria-label="Invite link"
              onFocus={(e) => e.currentTarget.select()}
            />
            <div className="waiting__buttons">
              <Tooltip content="Copy the invite link to send to your opponent">
                <button type="button" className="btn btn--primary waiting__copy" onClick={() => void copy()}>
                  {copied ? <IconCheck /> : <IconCopy />} {copied ? 'Copied' : 'Copy link'}
                </button>
              </Tooltip>
              {canShare() && (
                <Tooltip content="Send the invite link with another app">
                  <button type="button" className="btn" onClick={() => void shareRoom(view.code)}>
                    <IconShare /> Share
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          <div className="waiting__loom" role="status">
            <Loom />
            <p className="waiting__status">
              Waiting for your opponent<span className="waiting__ellipsis" aria-hidden="true" />
            </p>
            <p className="waiting__hint">Send them the link — the game starts the moment they join.</p>
          </div>

          <dl className="waiting__settings">
            <div>
              <dt>Host</dt>
              <dd>{me?.name ?? 'You'} (you)</dd>
            </div>
            <div>
              <dt>Turn timer</dt>
              <dd>{turnTimeLabel(turnTimeLimitSec)}</dd>
            </div>
            <div>
              <dt>Game ends</dt>
              <dd>
                After {passRoundsToEnd} {passRoundsToEnd === 1 ? 'pass' : 'passes'} each
              </dd>
            </div>
            <div>
              <dt>First move</dt>
              <dd>Chosen at random</dd>
            </div>
          </dl>

          <Tooltip content="Give up your seat and go back to the lobby">
            <button type="button" className="btn btn--ghost waiting__leave" onClick={() => leave({ forget: true })}>
              <IconDoor /> Leave room
            </button>
          </Tooltip>
        </section>
      </main>
      <footer className="app-footer">
        <VersionPill />
      </footer>
    </div>
  );
}
